import { useIsAdminUser } from "../hooks/useIsAdminUser";
import { useIsNnsrPortalUser } from "../hooks/useIsNnsrPortalUser";
import { useIsUserAuthenticated } from "../hooks/useIsUserAuthenticated";
import {
  authResultIsAdmin,
  authResultIsCnamUser,
  authResultIsPortalUser,
  authResultIsQueryUser,
} from "../utils/authUtils";
import { useEffect } from "react";
import { useNavigate } from "react-router";
import {
  EXPIRATION_LEAD_TIME_SECONDS,
  loadUserFromStorage,
  signinRedirect,
} from "./userServices";
import { User } from "oidc-client-ts";
import { EXPIRATION_LEAD_TIME_MS } from "./userServices";
import { useIsQueryPortalUser } from "../hooks/useIsQueryPortalUser";

/**
 * This class is responsible for any authentication logic for when a user initially logs into the portal.
 * If the user has a valid token and is on the login page, it'll push them to the correct case list page (admin or user).
 * If the user is on a page you need to be authenticated for without a token, it'll redirect them to saleforce to authenticate
 *
 * @returns Component
 */
const AutoSignin = () => {
  const navigate = useNavigate();
  const isMockAuth = process.env.REACT_APP_MOCK_AUTH === "true";

  let urlPath = window.location.href.substring(window.location.origin.length);
  let isLoginScreen = urlPath === "/";
  let isCallbackScreen = urlPath.includes("/callback");
  const isNnsrPortalUser = useIsNnsrPortalUser();
  const isAdminUser = useIsAdminUser();

  const navigateBasedOnCurrentPath = (user: User) => {
    // If the user has a valid token but is on the login page, redirect them accordingly
    if (isLoginScreen) {
      navigate(whereToRedirect(user));
      return;
    }

    // If the user is on an admin page but not an admin, redirect them accordingly
    if (!authResultIsAdmin(user) && urlPath.includes("admin")) {
      navigate(whereToRedirect(user));
      return;
    }

    // If the user is an admin and is NOT on an admin page, redirect them accordingly
    if (authResultIsAdmin(user) && !urlPath.includes("admin")) {
      navigate("/admin" + urlPath);
      return;
    }

    // If the user is NOT a qs portal user and tries to access those resources, redirect them accordingly
    if (!authResultIsQueryUser(user) && urlPath.includes("query-service")) {
      navigate(whereToRedirect(user));
      return;
    }

    // If the user is NOT a cnam portal user and tries to access those resources, redirect them accordingly
    if (!authResultIsCnamUser(user) && urlPath.includes("cnam")) {
      navigate(whereToRedirect(user));
      return;
    }

    // If the user is NOT an nnsr portal user and tries to access those resources, redirect them accordingly
    if (
      !authResultIsPortalUser(user) &&
      !authResultIsAdmin(user) &&
      !urlPath.includes("query-service")
    ) {
      navigate(whereToRedirect(user));
      return;
    }
  };

  useEffect(() => {
    // Skip authentication entirely when mock auth is enabled
    if (isMockAuth) {
      return;
    }

    const signinAsync = async () => {
      let user = await loadUserFromStorage();

      // User doesn't have any OIDC token in storage
      let currTime = Math.floor(new Date().getTime() / 1000);
      if (
        user !== null &&
        user.profile.exp - EXPIRATION_LEAD_TIME_SECONDS > currTime
      ) {
        navigateBasedOnCurrentPath(user); //The token is valid! It'll now be loaded into the store and we're good.
      } else if (!isLoginScreen && !isCallbackScreen) {
        // If the user doesn't have a valid user object in storage, or that user has expried, redirect them to the sign
        // in page
        signinRedirect();
      }
    };

    if (!isCallbackScreen && !isNnsrPortalUser() && !isAdminUser()) {
      signinAsync();
    }
  }, [isMockAuth]);

  return <></>;
};

function whereToRedirect(user: User): string {
  if (authResultIsAdmin(user)) {
    return "/admin/caselist";
  }
  if (authResultIsPortalUser(user)) {
    return "/caselist";
  }
  if (authResultIsQueryUser(user)) {
    return "/query-service/lookup";
  }

  if (authResultIsCnamUser(user)) {
    return "/cnam/request";
  }

  return "";
}

export default AutoSignin;
